import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { collectionsService } from '../../services/collectionsService';

const PublicCollection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams?.get('id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collection, setCollection] = useState(null);
  const [items, setItems] = useState([]);
  const [showcaseSettings, setShowcaseSettings] = useState(null);

  useEffect(() => {
    if (!collectionId) {
      setError('Collection ID is required');
      setLoading(false);
      return;
    }
    loadPublicCollection();
  }, [collectionId]);

  const loadPublicCollection = async () => {
    try {
      setLoading(true);
      setError('');

      const [collectionResult, itemsResult] = await Promise.all([
        collectionsService?.getPublicCollectionById(collectionId),
        collectionsService?.getPublicCollectionItems(collectionId)
      ]);

      if (collectionResult?.error) {
        throw new Error(collectionResult.error);
      }

      if (itemsResult?.error) {
        throw new Error(itemsResult.error);
      }

      setCollection(collectionResult?.data);
      setItems(itemsResult?.data || []);
      setShowcaseSettings(collectionResult?.data?.showcase_settings || {});

    } catch (err) {
      setError(err?.message || 'Failed to load collection');
    } finally {
      setLoading(false);
    }
  };

  const getCollectionIcon = (type) => {
    switch (type) {
      case 'vintage-watches': return 'Watch';
      case 'sports-cards': return 'CreditCard';
      case 'comics': return 'Book';
      case 'art-prints': return 'Image';
      case 'stamps': return 'Mail';
      case 'rare-coins': return 'Coins';
      case 'sneakers': return 'ShoppingBag';
      case 'toys': return 'Gamepad2';
      default: return 'Package';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading collection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Collection Not Found</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/')} iconName="Home" iconPosition="left">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back
            </Button>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-success/10 text-success">
              <Icon name="Eye" size={14} className="mr-1" />
              Public Collection
            </span>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon
                name={getCollectionIcon(collection?.collectible_type)}
                size={32}
                className="text-primary"
              />
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-card-foreground mb-2">
                {collection?.name}
              </h1>
              {showcaseSettings?.show_descriptions && collection?.description && (
                <p className="text-muted-foreground mb-4">
                  {collection?.description}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="Package" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Items</span>
                  </div>
                  <div className="text-2xl font-bold text-card-foreground">
                    {items?.length || 0}
                  </div>
                </div>

                {showcaseSettings?.show_values && (
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <Icon name="DollarSign" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Total Value</span>
                    </div>
                    <div className="text-2xl font-bold text-success">
                      ${(parseFloat(collection?.total_value) || 0)?.toLocaleString()}
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="Tag" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Type</span>
                  </div>
                  <div className="text-sm font-medium text-card-foreground capitalize">
                    {collection?.collectible_type?.replace('-', ' ')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Owner Info */}
          {showcaseSettings?.show_owner_info && collection?.user_profiles && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Collection by</p>
                  <p className="font-semibold text-card-foreground">
                    {collection?.user_profiles?.full_name}
                  </p>
                </div>
                {collection?.user_profiles?.membership_tier && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
                    {collection?.user_profiles?.membership_tier} Member
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Items Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {items?.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Package" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">This collection is empty</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items?.map((item) => (
              <div
                key={item?.id}
                className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Item Image */}
                <div className="aspect-square bg-muted/20 relative">
                  {item?.primary_image_url ? (
                    <Image
                      src={item?.primary_image_url}
                      alt={item?.name || 'Collection item'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Image" size={48} className="text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Item Details */}
                <div className="p-4">
                  <h3 className="font-semibold text-card-foreground mb-2 line-clamp-1">
                    {item?.name}
                  </h3>

                  {showcaseSettings?.show_descriptions && item?.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {item?.description}
                    </p>
                  )}

                  <div className="space-y-2">
                    {item?.brand && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Brand</span>
                        <span className="font-medium text-card-foreground">{item?.brand}</span>
                      </div>
                    )}

                    {item?.condition && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Condition</span>
                        <span className="font-medium text-card-foreground capitalize">
                          {item?.condition?.replace('-', ' ')}
                        </span>
                      </div>
                    )}

                    {showcaseSettings?.show_values && item?.current_value && (
                      <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                        <span className="text-muted-foreground">Value</span>
                        <span className="font-bold text-success">
                          ${parseFloat(item?.current_value)?.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicCollection;